import { useState } from "react";
import { CaretDown } from "@phosphor-icons/react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/collapsible";
import { TeamLogo } from "../shared/TeamLogo";
import { getNbaTeamInfo } from "../../constants/nbaTeams";

const STATE_BADGE = {
  normal: { label: "Model Projection", className: "border-eb-accent/40 bg-eb-accent/10 text-eb-accent" },
  withheld: { label: "Projection Withheld", className: "border-red-500/40 bg-red-500/10 text-red-400" },
  final: { label: "Final", className: "border-white/20 bg-white/5 text-zinc-300" },
  result_unavailable: { label: "Result Pending Sync", className: "border-yellow-500/40 bg-yellow-500/10 text-yellow-400" },
};

const pct0 = (x) => `${(x * 100).toFixed(0)}%`;
const pct1 = (x) => `${(x * 100).toFixed(1)}%`;
const fmtTipoff = (iso) => {
  if (!iso) return "Tipoff TBD";
  const d = new Date(iso);
  return d.toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" });
};

const MarketRow = ({ testId, label, modelText, bookText }) => (
  <div data-testid={testId} className="flex flex-col gap-0.5 font-mono text-[11px] text-zinc-300 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
    <div className="flex items-baseline gap-2 sm:contents">
      <span className="w-[72px] shrink-0 uppercase tracking-wide text-zinc-500">{label}</span>
      <span className="sm:flex-1">{modelText}</span>
    </div>
    <span className="pl-[72px] text-zinc-600 sm:pl-0 sm:shrink-0 sm:text-right sm:text-zinc-500">{bookText}</span>
  </div>
);

// Compact card mirroring NflPreviewCard.jsx's layout, adapted to basketball: no QB-scenario
// state (NBA has none). Current injury reports now MATERIALLY affect the projection via a
// redistributed expected rotation (see services/prediction/nba_availability.py) -- the
// Availability & Rotation section below shows the before/after swing, projected rotation, and
// any unresolved in/out scenarios.
export const NbaPreviewCard = ({ game }) => {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [availOpen, setAvailOpen] = useState(false);
  const badge = STATE_BADGE[game.state] || STATE_BADGE.withheld;
  const awayTeam = { ...getNbaTeamInfo(game.away_team), abbreviation: game.away_team };
  const homeTeam = { ...getNbaTeamInfo(game.home_team), abbreviation: game.home_team };

  const ml = game.probabilities?.moneyline;
  const spread = game.probabilities?.spread;
  const total = game.probabilities?.total;
  const odds = game.odds;
  const avail = game.availability;
  const scenarios = avail?.scenarios;

  return (
    <div data-testid={`nba-preview-card-${game.game_id}`} className="flex flex-col gap-3 rounded-lg border border-white/10 bg-eb-surface p-4 transition-colors hover:border-white/20">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-1.5 font-heading text-base text-white">
            <TeamLogo team={awayTeam} size={24} ringColor={awayTeam.color} testId={`nba-logo-away-${game.game_id}`} />
            <span>{awayTeam.name}</span>
            <span className="text-xs text-zinc-500">@</span>
            <TeamLogo team={homeTeam} size={24} ringColor={homeTeam.color} testId={`nba-logo-home-${game.game_id}`} />
            <span>{homeTeam.name}</span>
          </div>
          <span className="font-mono text-xs text-zinc-500">
            {fmtTipoff(game.tipoff_utc)}
            {game.venue && <> &middot; {game.venue}</>}
            {game.neutral_site && (
              <span data-testid={`nba-neutral-badge-${game.game_id}`} className="ml-1.5 rounded bg-white/10 px-1.5 py-0.5 text-[10px] uppercase text-zinc-300">
                Neutral Site
              </span>
            )}
          </span>
        </div>
        <span data-testid={`nba-state-badge-${game.game_id}`} className={`shrink-0 rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-wide ${badge.className}`}>
          {badge.label}
        </span>
      </div>

      <div data-testid={`nba-status-${game.game_id}`} className="rounded-md bg-black/20 px-3 py-2 font-mono text-xs">
        {game.status === "final" && game.final_score ? (
          <span data-testid={`nba-final-score-${game.game_id}`} className="text-sm text-zinc-200">
            Final &middot; {awayTeam.abbreviation} {game.final_score.away} &ndash; {homeTeam.abbreviation} {game.final_score.home}
            {game.overtime && <span className="ml-1 text-[10px] uppercase text-zinc-500">OT</span>}
          </span>
        ) : game.status === "result_unavailable" ? (
          <span className="text-yellow-400">Result pending sync</span>
        ) : game.projection ? (
          <span className="text-zinc-400" title={game.projection.margin_label}>
            Model: {awayTeam.abbreviation} {game.projection.away_points} &ndash; {homeTeam.abbreviation} {game.projection.home_points} &middot; margin{" "}
            {game.projection.home_margin > 0 ? "+" : ""}
            {game.projection.home_margin} &middot; total {game.projection.total}
          </span>
        ) : (
          <span className="text-zinc-600">No projection available</span>
        )}
      </div>

      {game.unresolved_availability && (
        <p data-testid={`nba-unresolved-availability-badge-${game.game_id}`} className="rounded-md border border-yellow-500/30 bg-yellow-500/5 px-3 py-1.5 font-mono text-[11px] text-yellow-400">
          Unresolved availability
          {[...(avail?.home?.material_uncertain_players || []), ...(avail?.away?.material_uncertain_players || [])].length > 0 ? (
            <>: {[...(avail?.home?.material_uncertain_players || []), ...(avail?.away?.material_uncertain_players || [])].map((p) => `${p.name} (${p.qualifier})`).join(", ")}</>
          ) : (
            <>: {[avail?.home?.thin_rotation && homeTeam.abbreviation, avail?.away?.thin_rotation && awayTeam.abbreviation].filter(Boolean).join(" & ")} rotation too thin to redistribute reliably</>
          )}
          {" "}&mdash; could still change this pick. See Availability &amp; Rotation below. Largest Leans withholds this game until it resolves.
        </p>
      )}

      {game.state === "normal" && ml && (
        <div data-testid={`nba-markets-${game.game_id}`} className="flex flex-col gap-1.5 rounded-md border border-eb-accent/20 bg-eb-accent/5 p-3">
          <MarketRow
            testId={`nba-ml-row-${game.game_id}`}
            label="Moneyline"
            modelText={`${awayTeam.abbreviation} ${pct0(ml.p_away_win)} / ${homeTeam.abbreviation} ${pct0(ml.p_home_win)}`}
            bookText={odds?.matched ? `${odds.sportsbook}${odds.stale ? " (stale)" : ""}` : "no book line"}
          />
          {spread && odds?.spread && (
            <MarketRow
              testId={`nba-spread-row-${game.game_id}`}
              label="Spread"
              modelText={`${homeTeam.abbreviation} ${spread.line > 0 ? "+" : ""}${spread.line} \u00b7 cover ${pct0(spread.p_home_cover)}`}
              bookText={`${odds.spread.home_price} / ${odds.spread.away_price}`}
            />
          )}
          {total && odds?.total && (
            <MarketRow
              testId={`nba-total-row-${game.game_id}`}
              label="Total"
              modelText={`${total.line} \u00b7 O ${pct0(total.p_over)} / U ${pct0(total.p_under)}`}
              bookText={`O ${odds.total.over_price} / U ${odds.total.under_price}`}
            />
          )}
          {!odds?.matched && (
            <p data-testid={`nba-odds-unmatched-${game.game_id}`} className="font-mono text-[10px] text-zinc-600">
              No bookmaker odds matched for this game yet &mdash; moneyline above uses the model's own margin distribution only (line=0).
            </p>
          )}
        </div>
      )}

      {game.state === "withheld" && game.withheld_reasons && (
        <div data-testid={`nba-withheld-reasons-${game.game_id}`} className="flex flex-col gap-1.5 rounded-md border border-red-500/20 bg-red-500/5 p-3">
          {game.withheld_reasons.map((r, i) => (
            <p key={i} className="font-mono text-[11px] leading-relaxed text-red-300">{r}</p>
          ))}
        </div>
      )}

      {game.status === "result_unavailable" && (
        <div data-testid={`nba-result-unavailable-${game.game_id}`} className="rounded-md border border-yellow-500/20 bg-yellow-500/5 p-3 font-mono text-[11px] leading-relaxed text-yellow-300">
          {game.result_unavailable_reason || "Result not yet available \u2014 tipoff has passed but the score hasn't synced yet."}
        </div>
      )}

      {avail && (
        <Collapsible open={availOpen} onOpenChange={setAvailOpen}>
          <CollapsibleTrigger
            data-testid={`nba-availability-toggle-${game.game_id}`}
            className="flex items-center gap-1.5 self-start font-mono text-[10px] uppercase tracking-wide text-zinc-500 transition-colors hover:text-zinc-300"
          >
            Availability &amp; Rotation <CaretDown size={10} className={`transition-transform ${availOpen ? "rotate-180" : ""}`} />
          </CollapsibleTrigger>
          <CollapsibleContent data-testid={`nba-availability-content-${game.game_id}`} className="flex flex-col gap-3 border-t border-white/5 pt-2">
            {game.projection && (
              <p className="font-mono text-[10px] text-zinc-500">
                Before availability: {awayTeam.abbreviation} {game.projection.away_points_before_availability} &ndash; {homeTeam.abbreviation} {game.projection.home_points_before_availability}
                {" "}&rarr; after: {awayTeam.abbreviation} {game.projection.away_points} &ndash; {homeTeam.abbreviation} {game.projection.home_points}
              </p>
            )}
            {scenarios && (
              <div data-testid={`nba-scenarios-${game.game_id}`} className="rounded-md border border-yellow-500/20 bg-yellow-500/5 p-2 font-mono text-[10px] text-yellow-300">
                <p>If uncertain player(s) play (IN): {awayTeam.abbreviation} {scenarios.in.away_points.toFixed(1)} &ndash; {homeTeam.abbreviation} {scenarios.in.home_points.toFixed(1)} &middot; total {scenarios.in.total.toFixed(1)}</p>
                <p>If they sit (OUT): {awayTeam.abbreviation} {scenarios.out.away_points.toFixed(1)} &ndash; {homeTeam.abbreviation} {scenarios.out.home_points.toFixed(1)} &middot; total {scenarios.out.total.toFixed(1)}</p>
              </div>
            )}
            {["away", "home"].map((side) => {
              const a = avail[side];
              const team = side === "home" ? homeTeam : awayTeam;
              if (!a) return null;
              const dayToDay = a.day_to_day || [];
              const unmatched = a.unmatched_reports || [];
              const out = a.out || [];
              const doubtful = a.doubtful || [];
              const questionable = a.questionable || [];
              const rotation = a.rotation || [];
              return (
                <div key={side} data-testid={`nba-avail-${side}-${game.game_id}`} className="flex flex-col gap-1 font-mono text-[10px] text-zinc-400">
                  <span className="uppercase tracking-wide text-zinc-500">{team.abbreviation}</span>
                  {out.length > 0 && <p>Out: {out.map((p) => p.player_name).join(", ")}</p>}
                  {doubtful.length > 0 && <p className="text-orange-300">Doubtful: {doubtful.map((p) => p.player_name).join(", ")}</p>}
                  {questionable.length > 0 && <p className="text-yellow-300">Questionable: {questionable.map((p) => p.player_name).join(", ")}</p>}
                  {dayToDay.length > 0 && <p className="text-yellow-300">Day-To-Day: {dayToDay.map((p) => p.player_name).join(", ")}</p>}
                  {unmatched.length > 0 && (
                    <p className="text-zinc-600">Unmatched report(s): {unmatched.map((p) => p.player_name).join(", ")} (couldn't be linked to a tracked roster player)</p>
                  )}
                  {a.thin_rotation && (
                    <p data-testid={`nba-thin-rotation-${side}-${game.game_id}`} className="text-red-400">
                      Thin rotation ({team.abbreviation}): fewer than 8 trackable players available &mdash; minutes forced onto too few real bodies, disclosed above.
                    </p>
                  )}
                  <table data-testid={`nba-rotation-table-${side}-${game.game_id}`} className="w-full table-fixed border-collapse text-[9px]">
                    <colgroup>
                      <col className="w-[34%]" /><col className="w-[26%]" /><col className="w-[18%]" /><col className="w-[22%]" />
                    </colgroup>
                    <tbody>
                      {rotation.map((p) => (
                        <tr key={p.athlete_id} className={p.available ? "text-zinc-400" : "text-red-400 line-through"}>
                          <td className="truncate pr-2">{p.name}</td>
                          <td className="truncate pr-2 text-zinc-500">{p.role.replace(/_/g, " ")}</td>
                          <td className="truncate pr-2 text-zinc-500">{p.lineup_status}</td>
                          <td className="text-right">{p.baseline_minutes}&rarr;{p.projected_minutes}m</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })}
            <p className="font-mono text-[9px] leading-relaxed text-zinc-600">{avail.home?.lineup_feed_notice}</p>
          </CollapsibleContent>
        </Collapsible>
      )}

      {ml && (
        <Collapsible open={detailsOpen} onOpenChange={setDetailsOpen}>
          <CollapsibleTrigger
            data-testid={`nba-details-toggle-${game.game_id}`}
            className="flex items-center gap-1.5 self-start font-mono text-[10px] uppercase tracking-wide text-zinc-500 transition-colors hover:text-zinc-300"
          >
            Model details <CaretDown size={10} className={`transition-transform ${detailsOpen ? "rotate-180" : ""}`} />
          </CollapsibleTrigger>
          <CollapsibleContent data-testid={`nba-details-content-${game.game_id}`} className="flex flex-col gap-2 border-t border-white/5 pt-2">
            <p className="font-mono text-[10px] text-zinc-600">
              Moneyline: {homeTeam.abbreviation} {pct1(ml.p_home_win)} / {awayTeam.abbreviation} {pct1(ml.p_away_win)} &middot; no tie possible (overtime always resolves it)
            </p>
            {spread && <p className="font-mono text-[10px] text-zinc-600">Spread fail/push: {pct1(spread.p_home_fail)} / {pct1(spread.p_push)}</p>}
            {total && <p className="font-mono text-[10px] text-zinc-600">Total push: {pct1(total.p_push)}</p>}
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
};
